import { cn } from 'noddi-ui-common';
import { isPastApiDate, isPastWeekHidden, NoddiDateFormats, noddiFormatDate, todayApiDate } from 'noddi-util';
import { NoddiSpinner } from '../../../Feedback/NoddiSpinner';
import { MonthCalendarNavigator } from './MonthCalendarNavigator';
import { MonthCalendarDayInfo, MonthCalendarProps } from './types';

const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

export const MonthCalendar = ({
  currentDate,
  onMonthChange,
  onDayClick,
  renderDayContent,
  todayLabel = 'Today',
  className,
  headerActions,
  cellSize = 'comfortable',
  loading = false,
  hideHeader = false,
  dayClassName,
  hidePastWeeks = false,
  onHidePastWeeksChange
}: MonthCalendarProps) => {
  const cellHeightClass = cellSize === 'compact' ? 'min-h-[72px]' : 'h-40';
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const firstDayOfMonth = (new Date(year, month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalCells = firstDayOfMonth + daysInMonth;
  const totalRows = Math.ceil(totalCells / 7);

  const todayString = todayApiDate();
  const apiDateOf = (day: number) => noddiFormatDate(new Date(year, month, day), NoddiDateFormats.API_DATE);
  const isRowHidden = (row: number) =>
    hidePastWeeks &&
    isPastWeekHidden({
      weekEnd: apiDateOf(Math.min(daysInMonth, row * 7 + 7 - firstDayOfMonth)),
      monthEnd: apiDateOf(daysInMonth),
      today: todayString
    });

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {!hideHeader && (
        <MonthCalendarNavigator
          currentDate={currentDate}
          onMonthChange={onMonthChange}
          todayLabel={todayLabel}
          hidePastDays={hidePastWeeks}
          onHidePastDaysChange={onHidePastWeeksChange}
          headerActions={headerActions}
        />
      )}

      <div className='relative w-full overflow-hidden rounded-lg border bg-white shadow-xs'>
        {loading && (
          <div className='absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-[1px]'>
            <NoddiSpinner />
          </div>
        )}
        <div className='grid grid-cols-7 border-b'>
          {WEEK_DAYS.map((day, index) => (
            <div
              key={day}
              className={cn(
                'bg-muted p-2 text-center text-xs font-semibold tracking-wide text-muted-foreground uppercase',
                index < 6 && 'border-r'
              )}
            >
              {day}
            </div>
          ))}
        </div>

        <div className='grid grid-cols-7'>
          {Array.from({ length: firstDayOfMonth }).map((_, i) => {
            const currentRow = Math.floor(i / 7);
            const isLastRow = currentRow === totalRows - 1;
            if (isRowHidden(currentRow)) {
              return null;
            }
            return (
              <div
                key={`empty-start-${i}`}
                className={cn('bg-muted/40', cellHeightClass, i % 7 < 6 && 'border-r', !isLastRow && 'border-b')}
              />
            );
          })}

          {Array.from({ length: daysInMonth }).map((_, idx) => {
            const day = idx + 1;
            const date = new Date(year, month, day);
            const dateString = noddiFormatDate(date, NoddiDateFormats.API_DATE);
            const cellIndex = firstDayOfMonth + idx;
            const isLastColumn = cellIndex % 7 === 6;
            const isLastRow = Math.floor(cellIndex / 7) === totalRows - 1;
            const isWeekend = cellIndex % 7 >= 5;
            const isToday = dateString === todayString;
            const isPast = isPastApiDate(dateString, todayString);

            if (isRowHidden(Math.floor(cellIndex / 7))) {
              return null;
            }

            const info: MonthCalendarDayInfo = {
              date,
              dateString,
              isInCurrentMonth: true,
              isWeekend,
              isToday,
              isPast
            };

            return (
              // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- month-grid day cell with an optional onDayClick; the interactive content inside each cell comes from the caller
              <div
                key={dateString}
                className={cn(
                  'flex flex-col gap-1 p-2 transition-colors',
                  cellHeightClass,
                  isPast ? 'bg-gray-100' : isWeekend ? 'bg-muted/30' : 'bg-white',
                  onDayClick && 'cursor-pointer hover:bg-muted',
                  !isLastColumn && 'border-r',
                  !isLastRow && 'border-b',
                  dayClassName?.(info)
                )}
                onClick={() => onDayClick?.(info)}
              >
                <div className='flex items-center justify-between'>
                  <span
                    className={cn(
                      'text-sm font-semibold',
                      isPast && 'text-muted-foreground',
                      isToday &&
                        'flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground'
                    )}
                  >
                    {day}
                  </span>
                </div>
                <div className='flex min-h-0 flex-1 flex-col gap-1'>{renderDayContent(info)}</div>
              </div>
            );
          })}

          {(() => {
            const trailing = totalRows * 7 - totalCells;
            return Array.from({ length: trailing }).map((_, i) => {
              const cellIndex = totalCells + i;
              const isLastColumn = cellIndex % 7 === 6;
              return (
                <div
                  key={`empty-end-${i}`}
                  className={cn('bg-muted/40', cellHeightClass, !isLastColumn && 'border-r')}
                />
              );
            });
          })()}
        </div>
      </div>
    </div>
  );
};
